import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks'
import { db } from '@/lib/firebase'
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { updateProfile } from 'firebase/auth'
import { ImageUpload } from '@/components/cms/image-upload'
import { 
  User as UserIcon, 
  Mail, 
  Shield, 
  Save, 
  Clock, 
  Camera,
  CheckCircle2,
  AlertCircle
} from 'lucide-react'

export function ProfilePage() {
  const { user, adminProfile } = useAuth()
  const [displayName, setDisplayName] = useState('')
  const [photoURL, setPhotoURL] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || adminProfile?.displayName || '')
      setPhotoURL(user.photoURL || adminProfile?.photoURL || '')
    }
  }, [user, adminProfile])

  const handleSave = async () => {
    if (!user) return
    setSaving(true)
    setMessage(null)

    try {
      // 1. Update Firebase Auth Profile
      await updateProfile(user, {
        displayName,
        photoURL
      })

      // 2. Update Firestore adminUsers doc
      const adminRef = doc(db, 'adminUsers', user.uid)
      await updateDoc(adminRef, {
        displayName,
        photoURL,
        updatedAt: serverTimestamp()
      })

      setMessage({ type: 'success', text: 'Profile updated successfully!' })
      
      // Auto-clear message after 3s
      setTimeout(() => setMessage(null), 3000)
    } catch (error: any) {
      console.error('Error updating profile:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to update profile.' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Profile Header Card */}
      <div className="relative overflow-hidden rounded-3xl border border-stone-800 bg-[#141414] p-8 shadow-2xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-orange-600/5 rounded-full blur-3xl -mr-32 -mt-32" />
        
        <div className="relative flex flex-col md:flex-row gap-8 items-center md:items-start">
          {/* Avatar Section */}
          <div className="relative group">
            <div className="w-32 h-32 rounded-3xl overflow-hidden border-4 border-stone-800 bg-stone-900 shadow-xl transition-transform group-hover:scale-[1.02]">
              {photoURL ? (
                <img src={photoURL} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-stone-800 to-stone-900">
                  <UserIcon className="w-12 h-12 text-stone-600" />
                </div>
              )}
            </div>
            <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-xl bg-orange-600 border-4 border-[#141414] flex items-center justify-center shadow-lg">
              <Camera className="w-4 h-4 text-white" />
            </div>
          </div>

          {/* Info Section */}
          <div className="flex-1 text-center md:text-left space-y-4">
            <div>
              <h2 className="text-3xl font-bold text-white tracking-tight">
                {displayName || 'User Profile'}
              </h2>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mt-2">
                <div className="flex items-center gap-2 text-stone-400 text-sm">
                  <Mail className="w-4 h-4" />
                  {user?.email}
                </div>
                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-bold uppercase tracking-wider">
                  <Shield className="w-3 h-3" />
                  {adminProfile?.role || 'Viewer'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Settings */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-3xl border border-stone-800 bg-[#141414] p-8 shadow-xl">
            <h3 className="text-xl font-bold text-white mb-6">Account Details</h3>
            
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-stone-400 flex items-center gap-2">
                   Display Name
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full px-5 py-3.5 rounded-2xl bg-[#0A0A0A] border border-stone-800 text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all placeholder:text-stone-700"
                  placeholder="Enter your name"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-stone-400 flex items-center gap-2">
                  Profile Picture
                </label>
                <ImageUpload 
                  value={photoURL}
                  onChange={setPhotoURL}
                  folder="admin-profiles"
                />
              </div>

              {message && (
                <div className={`p-4 rounded-2xl flex items-center gap-3 animate-in fade-in zoom-in-95 duration-200 ${
                  message.type === 'success' 
                    ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' 
                    : 'bg-rose-500/10 border border-rose-500/20 text-rose-400'
                }`}>
                  {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                  <span className="text-sm font-medium">{message.text}</span>
                </div>
              )}

              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full relative group overflow-hidden px-6 py-4 rounded-2xl bg-orange-600 text-white font-bold transition-all hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-orange-900/20"
              >
                <div className="relative flex items-center justify-center gap-2">
                  <Save className={`w-5 h-5 ${saving ? 'animate-spin' : ''}`} />
                  {saving ? 'Saving Changes...' : 'Save Profile'}
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar Info */}
        <div className="space-y-6">
          <div className="rounded-3xl border border-stone-800 bg-stone-900/30 p-6">
            <h3 className="text-sm font-bold text-stone-500 uppercase tracking-[0.2em] mb-4">Metadata</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-stone-800 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-stone-500" />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-stone-600 tracking-wider">Member Since</p>
                  <p className="text-sm text-stone-300">
                    {user?.metadata.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-stone-800 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-stone-500" />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-stone-600 tracking-wider">User UID</p>
                  <p className="text-[11px] font-mono text-stone-400 truncate w-32">{user?.uid}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
